import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, BarChart3, Trash2, Plus } from "lucide-react";
import useInventoryOwner from "../../hooks/useInventoryOwner";
import useBrandTranslation from "../../hooks/useBrandTranslation";
import useInventorySettings from "../../hooks/useInventorySettings";
import { fetchTreatments, deleteTreatment } from "../../apis/api.inventory";
import { queryInventoryTreatmentsKey } from "../../apis/queryKeys";
import { formatMoney } from "../../utils/inventoryFormat";
import customToast from "../common/Toaster";
import Button from "../common/Button";
import Spinner from "../common/Spinner";
import TreatmentStatsDialog from "./TreatmentStatsDialog";
import InventoryOwnerSelector from "./InventoryOwnerSelector";

export default function TreatmentList() {
  const { t } = useBrandTranslation();
  const { ownerId } = useInventoryOwner();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statsTreatment, setStatsTreatment] = useState(null);

  const { decimalSeparator, defaultCurrencySymbol } = useInventorySettings(ownerId);

  const { data: treatments, isLoading } = useQuery({
    queryKey: queryInventoryTreatmentsKey(ownerId),
    queryFn: () => fetchTreatments(ownerId),
    enabled: !!ownerId,
  });

  const deleteMutation = useMutation({
    mutationFn: (treatmentId) => deleteTreatment(treatmentId, ownerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      customToast(t("inventory.treatment.deleted", "Tratamiento eliminado"), "success");
    },
    onError: (e) => customToast(e?.message || "Error", "danger"),
  });

  const handleDelete = (tr) => {
    if (window.confirm(t("inventory.treatment.confirmDelete", "¿Estás seguro que deseas eliminar este tratamiento?"))) {
      deleteMutation.mutate(tr.id);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-3xl mx-auto">
        <Button variant="outline" onClick={() => navigate("/inventory")} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("inventory.back", "Volver")}
        </Button>

        <InventoryOwnerSelector className="mb-4" />

        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-gray-900">
            {t("inventory.treatment.listTitle", "Tratamientos")}
          </h1>
          <Button onClick={() => navigate("/inventory/treatments/new")}>
            <Plus className="w-4 h-4 mr-1" />
            {t("inventory.nav.newTreatment", "Tratamiento")}
          </Button>
        </div>

        <p className="mb-4 text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded-md p-3">
          {t(
            "inventory.treatment.intro",
            "No te preocupes por ir tan al detalle. Si a un paciente le asignas un tratamiento, te preguntaremos antes de guardar lo utilizado."
          )}
        </p>

        {isLoading ? (
          <Spinner className="w-10 h-10 mx-auto my-10" />
        ) : !treatments || treatments.length === 0 ? (
          <p className="text-gray-500 text-center py-10">
            {t("inventory.treatment.empty", "Aún no tienes tratamientos")}
          </p>
        ) : (
          <div className="space-y-2">
            {treatments.map((tr) => (
              <div
                key={tr.id}
                className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between"
              >
                <div className="min-w-0">
                  <p className="font-semibold text-gray-800 truncate">{tr.name}</p>
                  <p className="text-xs text-gray-500">
                    {(tr.items || []).length}{" "}
                    {t("inventory.treatment.supplies", "Insumos").toLowerCase()} ·{" "}
                    {formatMoney(tr.total_cost, defaultCurrencySymbol, decimalSeparator)}
                  </p>
                  {tr.updated_at && (
                    <p className="text-[11px] text-gray-400">
                      {t("inventory.treatment.lastUpdate", "Última actualización")}:{" "}
                      {new Date(tr.updated_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    title={t("inventory.treatment.stats", "Estadísticas")}
                    onClick={() => setStatsTreatment(tr)}
                    className="p-1.5 rounded hover:bg-gray-100 text-green-600"
                  >
                    <BarChart3 className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    title={t("inventory.treatment.edit", "Editar")}
                    onClick={() => navigate(`/inventory/treatments/${tr.id}/edit`)}
                    className="p-1.5 rounded hover:bg-gray-100 text-blue-600"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    title={t("inventory.treatment.delete", "Eliminar")}
                    onClick={() => handleDelete(tr)}
                    className="p-1.5 rounded hover:bg-gray-100 text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <TreatmentStatsDialog
          treatment={statsTreatment}
          ownerId={ownerId}
          decimalSeparator={decimalSeparator}
          currencySymbol={defaultCurrencySymbol}
          onClose={() => setStatsTreatment(null)}
        />
      </div>
    </div>
  );
}
